/**
 * Board-canvas camera state (F-121 board-first canvas, docs/04 §5, amended
 * 2026-07-10: CSS-transform zoom).
 *
 * The camera {x, y, zoom} is per-user VIEW state — zustand only, never CRDT
 * (two users panning are not editing the board). All board↔screen conversion
 * goes through @kola/shared `boardToScreen`/`screenToBoard` so the DOM table,
 * the floating-element overlay, and (later) comment pins share ONE transform
 * and can never misalign.
 *
 * Rendering applies the camera as ONE CSS transform on the board layer —
 * `translate(-x·zoom, -y·zoom) scale(zoom)` — so children render in board
 * units and the browser remaps pointer coordinates natively; `screenToBoard`
 * (divide by zoom) is used for pointer deltas and overlay math.
 */
import { clampZoom, screenToBoard, type Camera, type Point } from "@kola/shared";
import { createStore, type StoreApi } from "zustand/vanilla";

export interface ViewportSize {
  width: number;
  height: number;
}

export interface CanvasState {
  camera: Camera;
  /** Size of the canvas viewport in CSS px (kept fresh by a ResizeObserver). */
  viewport: ViewportSize;
  /**
   * True while a two-finger touch gesture (pinch zoom / pan) owns the
   * pointers. Interaction layers consult this inside their own pointer
   * handlers and abandon single-finger work (marquee, drags, strokes) the
   * moment a second finger lands — two fingers ALWAYS mean the camera.
   */
  pinching: boolean;
  setViewport(size: ViewportSize): void;
  setPinching(active: boolean): void;
  /** Pan so board content follows a pointer drag of (dx, dy) screen px. */
  panBy(dxScreen: number, dyScreen: number): void;
  /** Set zoom keeping the board point under `anchorScreen` fixed on screen. */
  zoomAt(anchorScreen: Point, zoom: number): void;
  /** Set zoom keeping the viewport center fixed (toolbar % control). */
  zoomAtCenter(zoom: number): void;
  /**
   * Jump to a camera outright — a share link's `?v=` (core/share-link.ts),
   * or the result of `fitCamera`. Zoom is clamped; x/y are taken as given.
   */
  setCamera(camera: Camera): void;
}

export type CanvasStore = StoreApi<CanvasState>;

/**
 * Pure zoom-about-a-point: returns the camera that has `nextZoom` and keeps
 * the board point currently under `anchorScreen` at the same screen position.
 */
export function zoomCameraAt(camera: Camera, anchorScreen: Point, nextZoom: number): Camera {
  const zoom = clampZoom(nextZoom);
  const anchorBoard = screenToBoard(camera, anchorScreen);
  return {
    zoom,
    x: anchorBoard.x - anchorScreen.x / zoom,
    y: anchorBoard.y - anchorScreen.y / zoom,
  };
}

/** Multiplicative zoom step for wheel notches / toolbar +/− (F-121). */
export const ZOOM_STEP = 1.2;

/** An axis-aligned board-unit rectangle — what `fitCamera` frames. */
export interface BoardRectLike {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Zoom ceiling when FRAMING an item (a share link's `?cell=` / `?el=`,
 * useShareDeepLink). Fitting a 120×28 cell into a laptop viewport would
 * otherwise slam the canvas to 500%, which reads as a mistake; 2× is "close
 * enough to read every word" while keeping the neighbours in view.
 */
export const FIT_MAX_ZOOM = 2;

/**
 * The camera that shows `rect` in the middle of `viewport` with `padding`
 * screen px of air around it, zoomed to fit but never past `maxZoom`. PURE.
 *
 * Fit-then-centre, in that order: the zoom is whatever makes the padded rect
 * fit both axes (clamped), and the rect's centre is then placed at the
 * viewport's centre at that zoom. A degenerate viewport (0×0, before the
 * ResizeObserver has run) keeps the current zoom and only centres.
 */
export function fitCamera(
  rect: BoardRectLike,
  viewport: ViewportSize,
  current: Camera,
  options: { padding?: number; maxZoom?: number } = {},
): Camera {
  const padding = options.padding ?? 48;
  const maxZoom = options.maxZoom ?? FIT_MAX_ZOOM;
  let zoom = current.zoom;
  const availW = viewport.width - padding * 2;
  const availH = viewport.height - padding * 2;
  if (availW > 0 && availH > 0 && rect.w > 0 && rect.h > 0) {
    zoom = clampZoom(Math.min(availW / rect.w, availH / rect.h, maxZoom));
  }
  return cameraCenteredOn({ x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 }, zoom, viewport);
}

/** The camera with board point `center` under the viewport's centre, at `zoom`. PURE. */
export function cameraCenteredOn(center: Point, zoom: number, viewport: ViewportSize): Camera {
  const z = clampZoom(zoom);
  return {
    zoom: z,
    x: center.x - viewport.width / 2 / z,
    y: center.y - viewport.height / 2 / z,
  };
}

/* Touch-interaction tuning (mobile controls — deliberately DIFFERENT from
 * desktop: one finger pans/taps, two fingers pinch the camera, long-press
 * replaces right-click, double-tap replaces double-click). */

/** Pointer travel (screen px) before a touch press stops being a tap. */
export const TOUCH_SLOP_SCREEN_PX = 8;
/**
 * True when the device's PRIMARY pointer is a finger (phone/tablet) — the JS
 * twin of the `@media (pointer: coarse)` CSS blocks, for affordances whose
 * geometry is computed inline per zoom (resize strips, element handles).
 */
export const COARSE_POINTER =
  typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches === true;
/** Hold duration for the long-press context menu (right-click equivalent). */
export const LONG_PRESS_MS = 500;
/** Two taps within this window (same target) = double-tap (edit a cell). */
export const DOUBLE_TAP_MS = 350;

/**
 * setPointerCapture that tolerates already-released or synthetic pointers
 * (throws NotFoundError otherwise — e.g. a drag that ends in the same tick,
 * or dispatched PointerEvents in tests).
 */
export function safeSetPointerCapture(target: Element, pointerId: number): void {
  try {
    target.setPointerCapture(pointerId);
  } catch {
    // Capture is an optimization (keeps fast drags on-target); losing it
    // degrades gracefully, so never let it take the interaction down.
  }
}

/** Default camera: a little margin so the grid origin isn't glued to the corner. */
export const DEFAULT_CAMERA: Readonly<Camera> = Object.freeze({ x: -64, y: -64, zoom: 1 });

export function createCanvasStore(initial: Camera = DEFAULT_CAMERA): CanvasStore {
  return createStore<CanvasState>((set, get) => ({
    camera: { ...initial },
    viewport: { width: 0, height: 0 },
    pinching: false,
    setViewport(size) {
      const current = get().viewport;
      if (current.width === size.width && current.height === size.height) return;
      set({ viewport: size });
    },
    setPinching(active) {
      if (get().pinching !== active) set({ pinching: active });
    },
    panBy(dxScreen, dyScreen) {
      const { camera } = get();
      set({
        camera: {
          ...camera,
          x: camera.x - dxScreen / camera.zoom,
          y: camera.y - dyScreen / camera.zoom,
        },
      });
    },
    zoomAt(anchorScreen, zoom) {
      set({ camera: zoomCameraAt(get().camera, anchorScreen, zoom) });
    },
    zoomAtCenter(zoom) {
      const { viewport } = get();
      get().zoomAt({ x: viewport.width / 2, y: viewport.height / 2 }, zoom);
    },
    setCamera(camera) {
      set({ camera: { x: camera.x, y: camera.y, zoom: clampZoom(camera.zoom) } });
    },
  }));
}
